import { useCallback, useMemo, useState } from "react";

import {
  ComplianceService,
  type FrameworkCode,
  type MultiFrameworkReadinessResponse,
} from "@/api";
import { useApi } from "@/hooks/use-api";
import { useTranslation } from "@/i18n";
import { parseApiError } from "@/lib/api-error";
import { formatFrameworkCode } from "@/lib/compliance-helpers";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

export function ComplianceGapsTab() {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<FrameworkCode | "">("");

  const fetcher = useCallback(
    () =>
      selected
        ? ComplianceService.getReadiness({ framework_code: selected })
        : ComplianceService.getReadiness(),
    [selected]
  );
  const { data, error, isLoading } =
    useApi<MultiFrameworkReadinessResponse>(fetcher, { deps: [selected] });

  const items = useMemo(() => data?.items ?? [], [data]);
  const available = useMemo(
    () => items.map((it) => it.framework_code),
    [items]
  );

  // If the user hasn't picked a framework yet, default the picker
  // value to the first one returned so the table has something to
  // show on first render.
  const effectiveCode = selected || available[0] || "";
  const active = items.find((it) => it.framework_code === effectiveCode);

  return (
    <div className="space-y-4">
      {error && <Alert variant="warning">{parseApiError(error)}</Alert>}

      <div className="flex flex-col gap-2 tablet:flex-row tablet:items-center">
        <label
          htmlFor="gap-framework"
          className="text-sm font-medium text-ocean-900"
        >
          {t("compliance.gaps.frameworkLabel")}
        </label>
        <select
          id="gap-framework"
          value={effectiveCode}
          onChange={(e) => setSelected(e.target.value as FrameworkCode)}
          className={cn(
            "h-9 rounded-md border border-ocean-100 bg-white px-3 text-sm",
            "focus:outline-none focus:ring-2 focus:ring-ocean-400"
          )}
        >
          {available.length === 0 && (
            <option value="">
              {t("compliance.gaps.noActiveFramework")}
            </option>
          )}
          {available.map((code) => (
            <option key={code} value={code}>
              {formatFrameworkCode(code)}
            </option>
          ))}
        </select>
        {active && (
          <span className="text-xs text-neutral-500 tablet:ml-2">
            {t("compliance.gaps.gapCount", {
              count: active.gap_clauses.length,
            })}
          </span>
        )}
      </div>

      <div className="overflow-hidden rounded-lg border border-ocean-100 bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("compliance.gaps.colClauseCode")}</TableHead>
              <TableHead>{t("compliance.gaps.colTitle")}</TableHead>
              <TableHead className="hidden tablet:table-cell">
                {t("compliance.gaps.colCategory")}
              </TableHead>
              <TableHead>{t("compliance.gaps.colPriority")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && items.length === 0 && (
              <>
                {[0, 1, 2].map((i) => (
                  <TableRow key={`gap-sk-${i}`}>
                    <TableCell colSpan={4} className="py-4">
                      <Skeleton className="h-5 w-full" />
                    </TableCell>
                  </TableRow>
                ))}
              </>
            )}
            {!isLoading && !active && (
              <TableEmpty
                colSpan={4}
                message={t("compliance.gaps.noFramework")}
              />
            )}
            {active && active.gap_clauses.length === 0 && (
              <TableEmpty
                colSpan={4}
                message={t("compliance.gaps.noGaps")}
              />
            )}
            {active?.gap_clauses.map((gap) => (
              <TableRow key={gap.clause_id}>
                <TableCell className="whitespace-nowrap font-mono text-xs text-ocean-700">
                  {gap.code}
                </TableCell>
                <TableCell className="font-medium text-ocean-900">
                  {gap.title}
                </TableCell>
                <TableCell className="hidden tablet:table-cell text-neutral-600">
                  {gap.category ?? "—"}
                </TableCell>
                <TableCell>
                  <PriorityBadge weight={gap.priority_weight} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <p className="text-xs text-neutral-500">
        {t("compliance.gaps.note")}
      </p>
    </div>
  );
}

function PriorityBadge({ weight }: { weight: number }) {
  const { t } = useTranslation();
  if (weight >= 2) {
    return <Badge tone="danger">{t("compliance.priority.critical")}</Badge>;
  }
  if (weight >= 1.5) {
    return <Badge tone="warning">{t("compliance.priority.high")}</Badge>;
  }
  return <Badge tone="neutral">{t("compliance.priority.normal")}</Badge>;
}
